<template>
  <div :class="colSpan">
    <div class="mb-2 block text-grey-300 typo-subtitle">
      {{ shouldTranslate ? $t(`forms.${field.handle}`) : (label || field.label || field.display) }}{{ isRequired ? '*' : '' }}
    </div>

    <Field
      v-slot="{ handleChange, value }"
      :name="name || handle || field.handle"
      :label="label || field.label || field.display"
      :rules="`${rulesAsString}|mimes:image/jpeg,image/png,application/pdf`"
    >
      <div class="flex items-start space-y-2">
        <input
          :id="`${field.handle}-actual-btn`"
          type="file"
          hidden
          :multiple="multiple"
          @change="handleChange"
        />
        <!-- Our custom upload button -->
        <label
          :for="`${field.handle}-actual-btn`"
          class="
            w-full cursor-pointer border-b bg-grey-100 p-3 transition-colors
            focus:border-black
            hover:bg-grey-200
          "
          :class="error ? 'border-error' : 'border-black'"
        >
          <!-- Name of file chosen -->
          <span
            v-if="!multiple"
            id="file-chosen"
            class="mt-4 break-all sm:ml-5 sm:mt-0"
          >
            {{ value?.name || initialFileName }}
          </span>
          <template v-else>
            <span
              v-for="(file, index) in value"
              :id="`file-chosen-${index}`"
              :key="index"
              class="mt-2 flex items-center break-all font-semibold"
            >
              {{ file.name }}
            </span>
          </template>

          <div class="ml-auto flex size-8 items-center justify-center bg-primary-900">
            <IconUpload class="size-5 text-white" />
          </div>
        </label>
      </div>
    </Field>

    <!-- ERROR -->
    <FieldError :error="error" />
  </div>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { Field } from 'vee-validate'

const props = defineProps({
  field: {
    type: Object,
    default: null,
  },

  label: {
    type: String,
    default: '',
  },

  handle: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  btnLabel: {
    type: String,
    default: null,
  },

  rules: {
    type: [Array, String],
    default: '',
  },

  error: {
    type: String,
    default: null,
  },

  instructions: {
    type: String,
    default: null,
  },

  initialFileName: {
    type: String,
    default: '',
  },
})
const shouldTranslate = inject('shouldTranslate', false)

const multiple = props.field?.config?.max_files !== 1

// filter out rules min and max for files
let { rules } = props

rules = rules.filter((rule) => {
  if (typeof rule === 'string') {
    return !rule.includes('min') && !rule.includes('max')
  }
  return true
})

// add custom rule to check amount max files
const maxFiles = props.field?.config?.max_files
if (maxFiles) {
  rules.push(`max_files:${maxFiles}`)
}

// add custom rule to check amount min files
const minFiles = props.field?.config?.min_files
if (minFiles && minFiles > 0) {
  // Then the field is required
  if (!rules.includes('required')) {
    rules.push('required')
  }

  rules.push(`min_files:${minFiles}`)
}

const rulesAsString = useFilterVeeValidateRules(rules)

const isRequired = computed(() => {
  return props.rules.includes('required')
})

const colSpan = computed(() => {
  const width = parseInt(props.width) || props.field?.width || 100

  switch (width) {
    case 25:
      return 'md:col-span-6 lg:col-span-3 col-span-12'
    case 33:
      return 'md:col-span-6 lg:col-span-4 col-span-12'
    case 50:
      return 'md:col-span-6 lg:col-span-6 col-span-12'
    case 66:
      return 'md:col-span-6 lg:col-span-8 col-span-12'
    default:
      return 'lg:col-span-12 col-span-12'
  }
})
</script>
